import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Lightbulb, RefreshCw, Star, Copy, ChevronDown, Heart,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  presence:  { label: "Presence",  color: "#7c3aed", bg: "bg-violet-500/10",  text: "text-violet-300" },
  listening: { label: "Listening", color: "#10B981", bg: "bg-emerald-500/10", text: "text-emerald-300" },
  decision:  { label: "Decision",  color: "#3B82F6", bg: "bg-blue-500/10",    text: "text-blue-300" },
  courage:   { label: "Courage",   color: "#F59E0B", bg: "bg-amber-500/10",   text: "text-amber-300" },
};

/**
 * Pre-Meeting Reflection card. Pass `meetingId` (optional) so the same
 * reflection sticks to that meeting until the user explicitly cycles to a
 * new one — that way Niko OS isn't randomly swapping the reflection on
 * every re-render right before they walk into the room.
 */
export default function ReflectionCard({ meetingId = null, compact = false }) {
  const [reflection, setReflection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("any"); // any | presence | listening | decision | courage
  const [showFilter, setShowFilter] = useState(false);

  const fetchOne = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "any") params.category = filter;
      if (opts.exclude && reflection) params.exclude_id = reflection.id;
      // Use meetingId as a stable seed so the same meeting shows the same
      // reflection across reloads — unless the user clicks "new".
      if (meetingId && !opts.exclude) params.seed = meetingId;
      const res = await axios.get(`${API}/reflections/random`, {
        params, withCredentials: true,
      });
      setReflection(res.data);
    } catch (_) {
      toast.error("Couldn't load a reflection");
    } finally { setLoading(false); }
  }, [filter, reflection, meetingId]);

  useEffect(() => {
    fetchOne();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, meetingId]);

  const toggleFav = async () => {
    if (!reflection) return;
    try {
      const res = await axios.post(`${API}/reflections/favorite`,
        { reflection_id: reflection.id }, { withCredentials: true });
      setReflection({ ...reflection, is_favorite: res.data.is_favorite });
      toast.success(res.data.is_favorite ? "Saved to favorites" : "Removed from favorites");
    } catch (_) { toast.error("Couldn't update favorite"); }
  };

  const copy = async () => {
    if (!reflection) return;
    try {
      await navigator.clipboard.writeText(reflection.text);
      toast.success("Reflection copied");
    } catch (_) { toast.error("Couldn't copy"); }
  };

  const cat = reflection ? CATEGORY_META[reflection.category] : null;

  return (
    <Card
      className={`overflow-hidden border-violet-500/20 bg-gradient-to-br from-violet-500/5 via-zinc-900/60 to-zinc-900/40`}
      data-testid="reflection-card"
    >
      <CardContent className={compact ? "p-3" : "p-4 sm:p-5"}>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-violet-500/15 flex items-center justify-center flex-shrink-0">
            <Lightbulb className="w-5 h-5 text-violet-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                Pre-Meeting Reflection
              </span>
              {cat && (
                <Badge
                  variant="outline"
                  className={`${cat.bg} ${cat.text} border-0 text-[10px] uppercase tracking-wider`}
                >
                  {cat.label}
                </Badge>
              )}
              {reflection?.is_favorite && (
                <Heart className="w-3 h-3 text-pink-400 fill-pink-400" />
              )}
            </div>
            {loading || !reflection ? (
              <div className="h-12 bg-zinc-800/40 rounded animate-pulse" />
            ) : (
              <>
                <p className={`leading-snug text-zinc-100 ${compact ? "text-sm" : "text-base"} font-medium`}>
                  "{reflection.text}"
                </p>
                {reflection.source && (
                  <p className="text-[11px] text-zinc-500 mt-1">— {reflection.source}</p>
                )}
              </>
            )}

            {/* Action row */}
            <div className="flex items-center gap-1.5 mt-3 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={() => fetchOne({ exclude: true })}
                disabled={loading}
                data-testid="reflection-new-btn"
                className="text-xs h-8"
              >
                <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} />
                New
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={toggleFav}
                disabled={!reflection}
                data-testid="reflection-fav-btn"
                className={`text-xs h-8 ${reflection?.is_favorite ? "text-pink-400 border-pink-500/40" : ""}`}
                title={reflection?.is_favorite ? "Remove from favorites" : "Save to favorites"}
              >
                <Star className={`w-3 h-3 mr-1 ${reflection?.is_favorite ? "fill-pink-400 text-pink-400" : ""}`} />
                {reflection?.is_favorite ? "Saved" : "Save"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={copy}
                disabled={!reflection}
                data-testid="reflection-copy-btn"
                className="text-xs h-8"
                title="Copy to clipboard so you can paste into chat or notes"
              >
                <Copy className="w-3 h-3 mr-1" />
                Copy
              </Button>

              {/* Category filter */}
              <div className="relative ml-auto">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowFilter(v => !v)}
                  data-testid="reflection-filter-btn"
                  className="text-xs h-8"
                >
                  {filter === "any" ? "Any category" : CATEGORY_META[filter].label}
                  <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
                {showFilter && (
                  <div className="absolute right-0 top-full mt-1 z-50 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl py-1 min-w-[140px]">
                    {[["any", "Any category"], ...Object.entries(CATEGORY_META).map(([k, v]) => [k, v.label])].map(([k, label]) => (
                      <button
                        key={k}
                        onClick={() => { setFilter(k); setShowFilter(false); }}
                        className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-800
                          ${filter === k ? "text-violet-300 font-semibold" : "text-zinc-300"}`}
                        data-testid={`reflection-cat-${k}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
